/**
 * T052: Prompts Service
 * API methods for prompt operations
 */

import { apiGet, apiPost, apiPatch, apiDelete } from './api'
import type {
  PromptResponse,
  PromptListResponse,
  CategoryResponse,
  TagResponse,
  PromptContent,
  PromptConfig,
  PromptMetadata,
} from '@arcanum/core'

// Re-export API types for use in components
export type { PromptResponse, CategoryResponse, TagResponse, PromptContent, PromptConfig, PromptMetadata }

// Frontend filter params
export interface PromptFilters {
  page?: number
  limit?: number
  categoryId?: string
  tagIds?: string[]
  isTemplate?: boolean
  q?: string
}

// Input types for mutations
export interface CreatePromptInput {
  title: string
  content: PromptContent
  category_id?: string | null
  tag_ids?: string[]
  config?: PromptConfig | null
  metadata?: PromptMetadata | null
}

export interface UpdatePromptInput {
  title?: string
  content?: PromptContent
  category_id?: string | null
  tag_ids?: string[]
  config?: PromptConfig | null
  metadata?: PromptMetadata | null
}

export interface GeneratePromptInput {
  variables: Record<string, string>
}

export interface VariableInfo {
  name: string
  default_value: string | null
  required: boolean
}

export interface PromptVariablesResponse {
  prompt_id: string
  is_template: boolean
  variables: VariableInfo[]
}

export interface GeneratePromptResponse {
  prompt_id: string
  title: string
  original_content: PromptContent
  rendered_content: PromptContent
  variables_used: {
    name: string
    value: string
    default_value: string | null
  }[]
  config: PromptConfig | null
}

/**
 * List prompts with optional filters and pagination
 */
export async function listPrompts(params?: PromptFilters): Promise<PromptListResponse> {
  const apiParams: Record<string, string | number | boolean | string[] | undefined> = {}

  if (params?.page) apiParams.page = params.page
  if (params?.limit) apiParams.limit = params.limit
  if (params?.categoryId) apiParams.category_id = params.categoryId
  if (params?.tagIds) apiParams.tag_ids = params.tagIds.join(',')
  if (params?.isTemplate !== undefined) apiParams.is_template = params.isTemplate
  if (params?.q) apiParams.q = params.q

  return apiGet<PromptListResponse>('/api/v1/prompts', apiParams)
}

/**
 * Get a single prompt by ID
 */
export async function getPrompt(id: string): Promise<PromptResponse> {
  return apiGet<PromptResponse>(`/api/v1/prompts/${id}`)
}

/**
 * Create a new prompt
 */
export async function createPrompt(input: CreatePromptInput): Promise<PromptResponse> {
  return apiPost<PromptResponse>('/api/v1/prompts', input)
}

/**
 * Update an existing prompt
 */
export async function updatePrompt(
  id: string,
  input: UpdatePromptInput
): Promise<PromptResponse> {
  return apiPatch<PromptResponse>(`/api/v1/prompts/${id}`, input)
}

/**
 * Delete a prompt
 */
export async function deletePrompt(id: string): Promise<void> {
  return apiDelete(`/api/v1/prompts/${id}`)
}

/**
 * Get variables for a template prompt
 */
export async function getPromptVariables(id: string): Promise<PromptVariablesResponse> {
  return apiGet<PromptVariablesResponse>(`/api/v1/prompts/${id}/variables`)
}

/**
 * Generate rendered prompt from a template
 */
export async function generatePrompt(
  id: string,
  input: GeneratePromptInput
): Promise<GeneratePromptResponse> {
  return apiPost<GeneratePromptResponse>(`/api/v1/prompts/${id}/generate`, input)
}
